import {
  forwardRef,
  ChangeEvent,
  Ref,
  useRef,
  useImperativeHandle,
} from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import Button from '@decisiv/ui-components/lib/components/Button';

import { Input } from './styles';

interface FileInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'defaultValue'> {
  name: string;
  defaultValue?: FileList | null;
  shouldUnregister?: boolean;
  buttonText?: string;
}

const FileInput = forwardRef<HTMLInputElement, FileInputProps>(
  (
    {
      name,
      buttonText,
      defaultValue = null,
      shouldUnregister,
      ...props
    }: FileInputProps,
    ref: Ref<HTMLInputElement>,
  ) => {
    const internalRef = useRef<HTMLInputElement>(null);
    const { control } = useFormContext();

    useImperativeHandle(
      ref,
      /* istanbul ignore next -- @preserve */ () =>
        internalRef.current as HTMLInputElement,
      [internalRef],
    );

    return (
      <Controller
        name={name}
        control={control}
        shouldUnregister={shouldUnregister}
        defaultValue={defaultValue}
        render={({ field: { onChange } }) => {
          const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
            onChange(e.target.files);
          };

          return (
            <>
              <Input
                ref={internalRef}
                type="file"
                onChange={handleOnChange}
                {...props}
              />
              <Button
                kind="secondary"
                type="button"
                onClick={() => internalRef?.current?.click()}
                text={buttonText}
              />
            </>
          );
        }}
      />
    );
  },
);

FileInput.displayName = 'FileInput';

export default FileInput;
